import type { GetMessagesQuery } from '#src/graphql/generated';

import { Alert, Box, Spinner, Text, VStack } from '@chakra-ui/react';

import {
  MessageCard,
  type AskAuditNoteOptions,
  type MessageCardVariant,
} from '#src/components/message-card';

export type MessageListMessage = GetMessagesQuery['messages'][number];

export type { AskAuditNoteOptions };

export interface MessageListProps {
  messages: MessageListMessage[];
  loading: boolean;
  error: Error | undefined;
  emptyMessage?: string;
  /** Resolved thread root when URL has `focusMessageId`; drives expanded vs collapsed UI. */
  focusedRootId: string | null;
  onExpandThread: (rootId: string) => void;
  /** When set, a sentinel is mounted for scroll-to-end (see dashboard). */
  messagesEndRef?: React.RefObject<HTMLDivElement | null>;
  /** AI audit page only. */
  onRunAskAuditNote?: (
    messageId: string,
    query: string,
    options?: AskAuditNoteOptions,
  ) => Promise<void>;
  askAuditNoteLoadingId?: string | null;
  /** When true, messages sit at the bottom of the scroll area (e.g. above a fixed chat input). */
  alignMessagesToBottom?: boolean;
  /** Called after a message is soft-deleted so the parent can refetch the list. */
  onMessageDeleted?: () => void | Promise<void>;
}

export function MessageList({
  messages,
  loading,
  error,
  emptyMessage = 'No messages found.',
  focusedRootId,
  onExpandThread,
  messagesEndRef,
  onRunAskAuditNote,
  askAuditNoteLoadingId = null,
  alignMessagesToBottom = false,
  onMessageDeleted,
}: MessageListProps) {
  const variant: MessageCardVariant = focusedRootId ? 'expanded' : 'collapsed';

  if (loading) {
    return (
      <Box textAlign="center" py={8}>
        <Spinner size="lg" color="blue.500" />
        <Text fontSize="sm" color="fg.muted" mt={4}>
          Loading messages...
        </Text>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert.Root status="error">
        <Alert.Indicator />
        <Alert.Title>Error loading messages: {error.message}</Alert.Title>
      </Alert.Root>
    );
  }

  if (messages.length === 0) {
    if (alignMessagesToBottom) {
      return (
        <Box flex={1} minHeight={0} overflowY="auto" width="100%">
          <Box
            minHeight="100%"
            display="flex"
            flexDirection="column"
            justifyContent="flex-end"
          >
            <Text color="fg.muted" textAlign="center" py={4}>
              {emptyMessage}
            </Text>
          </Box>
        </Box>
      );
    }
    return (
      <Text color="fg.muted" textAlign="center" py={8}>
        {emptyMessage}
      </Text>
    );
  }

  const list = (
    <VStack
      gap={3}
      align="stretch"
      minHeight={alignMessagesToBottom ? '100%' : undefined}
      justifyContent={alignMessagesToBottom ? 'flex-end' : undefined}
      px={4}
      /* Default flex-shrink:1 on column flex items squashes cards to fit the viewport so the
         scroll area never grows — dashboard appears “not scrollable”. */
      css={{ '& > *': { flexShrink: 0 } }}
    >
      {messages.map((message) => (
        <MessageCard
          key={message.id}
          message={message}
          childMessages={message.childMessages}
          variant={variant}
          onExpandThread={variant === 'collapsed' ? onExpandThread : undefined}
          onRunAskAuditNote={onRunAskAuditNote}
          askAuditNoteLoadingId={askAuditNoteLoadingId}
          onMessageDeleted={onMessageDeleted}
        />
      ))}
      {messagesEndRef ? <Box ref={messagesEndRef} /> : null}
    </VStack>
  );

  if (!alignMessagesToBottom) {
    return list;
  }

  return (
    <Box flex={1} minHeight={0} overflowY="auto" width="100%">
      {list}
    </Box>
  );
}
